import React, { useState, useMemo } from 'react';
import {
    Box,
    IconButton,
    Slider,
    Typography,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from '@mui/material';
import {
    PlayArrow,
    Pause,
    SkipNext,
    Stop,
} from '@mui/icons-material';
import { vortxColors, retroColors, vortxTypography } from '@vortx/shared-ui';

const SPEED_PRESETS = [0.5, 1, 10, 100, 1000];

// Logarithmic scale mapping for the slider
const logScale = (value) => {
    const minLog = Math.log10(SPEED_PRESETS[0]);
    const maxLog = Math.log10(SPEED_PRESETS[SPEED_PRESETS.length - 1]);
    return Math.pow(10, minLog + (value / 100) * (maxLog - minLog));
};

const inverseLogScale = (speed) => {
    const minLog = Math.log10(SPEED_PRESETS[0]);
    const maxLog = Math.log10(SPEED_PRESETS[SPEED_PRESETS.length - 1]);
    return ((Math.log10(speed) - minLog) / (maxLog - minLog)) * 100;
};

const speedMarks = SPEED_PRESETS.map((preset) => ({
    value: inverseLogScale(preset),
    label: preset >= 1 ? `${preset}x` : `${preset}x`,
}));

/**
 * Format a simulation timestamp for display.
 * Expects state.current_time as ISO string or Unix epoch seconds.
 */
const formatSimTime = (currentTime) => {
    if (!currentTime) return '---';
    try {
        const date = typeof currentTime === 'number'
            ? new Date(currentTime * 1000)
            : new Date(currentTime);
        return date.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        }) + ' ' + date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
    } catch {
        return '---';
    }
};

/**
 * SimulationControls - Retro-styled control bar for the Digital Twin simulation.
 *
 * @param {{ state: object|null, connected: boolean, onSend: function }} props
 */
export default function SimulationControls({ state, connected, onSend }) {
    const [stopDialogOpen, setStopDialogOpen] = useState(false);
    const [sliderValue, setSliderValue] = useState(() => inverseLogScale(1));

    const isPaused = state?.status === 'paused';
    const isRunning = state?.status === 'running' || state?.status === 'paused';
    const isMpc = state?.mpc_mode === true;
    const isRealTime = (state?.speed ?? 1) <= 1;
    const currentDay = state?.day ?? 0;
    const totalDays = 365;
    const currentSpeed = state?.speed ?? 1;

    // Derive a display speed label from the actual speed
    const speedLabel = useMemo(() => {
        if (isMpc) return 'MPC';
        if (currentSpeed >= 1) {
            return currentSpeed >= 100 ? `${Math.round(currentSpeed)}x` : `${currentSpeed}x`;
        }
        return `${currentSpeed}x`;
    }, [currentSpeed, isMpc]);

    const handlePlayPause = () => {
        if (!connected) return;
        onSend(isPaused ? 'resume' : 'pause');
    };

    const handleStep = () => {
        if (!connected || !isPaused) return;
        onSend('step');
    };

    const handleSpeedChange = (_event, newValue) => {
        setSliderValue(newValue);
        const newSpeed = Math.round(logScale(newValue) * 10) / 10;
        onSend('set_speed', { speed: newSpeed });
    };

    const handleStop = () => {
        setStopDialogOpen(false);
        onSend('stop');
    };

    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 2,
                    py: 1,
                    backgroundColor: retroColors.surface,
                    borderTop: `1px solid ${vortxColors.teal}`,
                    borderRadius: '0 0 4px 4px',
                    flexWrap: 'wrap',
                    minHeight: 52,
                }}
            >
                {/* Play / Pause — hidden at real-time speed (can't pause real time) */}
                {!isRealTime && (
                    <Tooltip title={isPaused ? 'Play' : 'Pause'}>
                        <span>
                            <IconButton
                                onClick={handlePlayPause}
                                disabled={!connected || !isRunning}
                                size="small"
                                sx={{
                                    color: retroColors.accent,
                                    '&:hover': { backgroundColor: `${retroColors.accent}22` },
                                    '&.Mui-disabled': { color: `${retroColors.accent}44` },
                                }}
                            >
                                {isPaused ? <PlayArrow /> : <Pause />}
                            </IconButton>
                        </span>
                    </Tooltip>
                )}

                {/* Step Forward — only available when paused (accelerated mode) */}
                {!isRealTime && (
                    <Tooltip title="Step forward (1 timestep)">
                        <span>
                            <IconButton
                                onClick={handleStep}
                                disabled={!connected || !isPaused || !isRunning}
                                size="small"
                                sx={{
                                    color: retroColors.accent,
                                    '&:hover': { backgroundColor: `${retroColors.accent}22` },
                                    '&.Mui-disabled': { color: `${retroColors.accent}44` },
                                }}
                            >
                                <SkipNext />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}

                {/* Divider */}
                <Box sx={{ width: '1px', height: 28, backgroundColor: `${vortxColors.teal}66` }} />

                {/* Speed Slider — always shown */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 200 }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.65rem',
                        color: retroColors.muted,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        whiteSpace: 'nowrap',
                    }}>
                        Speed
                    </Typography>
                    <Slider
                        value={sliderValue}
                        onChange={handleSpeedChange}
                        min={0}
                        max={100}
                        step={1}
                        marks={speedMarks}
                        disabled={!connected}
                        sx={{
                            width: 140,
                                color: retroColors.accent,
                                '& .MuiSlider-markLabel': {
                                    fontFamily: vortxTypography.mono,
                                    fontSize: '0.55rem',
                                    color: retroColors.dimmed,
                                },
                                '& .MuiSlider-thumb': {
                                    width: 14,
                                    height: 14,
                                    '&:hover, &.Mui-focusVisible': {
                                        boxShadow: `0 0 8px ${retroColors.accent}`,
                                    },
                                },
                                '& .MuiSlider-track': {
                                    height: 3,
                                },
                                '& .MuiSlider-rail': {
                                    height: 3,
                                    backgroundColor: `${vortxColors.teal}44`,
                                },
                            }}
                        />
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.75rem',
                            color: vortxColors.gold,
                            fontWeight: 600,
                            minWidth: 40,
                            textAlign: 'right',
                        }}>
                            {speedLabel}
                        </Typography>
                    </Box>

                {/* Divider */}
                <Box sx={{ width: '1px', height: 28, backgroundColor: `${vortxColors.teal}66` }} />

                {/* Time Display */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.8rem',
                        color: vortxColors.white,
                        letterSpacing: '0.03em',
                    }}>
                        {formatSimTime(state?.sim_time)}
                    </Typography>

                    {/* Day Counter */}
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.7rem',
                        color: retroColors.dimmed,
                    }}>
                        Day {currentDay} / {totalDays}
                    </Typography>
                </Box>

                {/* Spacer */}
                <Box sx={{ flex: 1 }} />

                {/* Stop Button */}
                <Tooltip title="Stop simulation">
                    <span>
                        <IconButton
                            onClick={() => setStopDialogOpen(true)}
                            disabled={!connected || !isRunning}
                            size="small"
                            sx={{
                                color: vortxColors.error,
                                '&:hover': { backgroundColor: `${vortxColors.error}22` },
                                '&.Mui-disabled': { color: `${vortxColors.error}44` },
                            }}
                        >
                            <Stop />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            {/* Stop Confirmation Dialog */}
            <Dialog
                open={stopDialogOpen}
                onClose={() => setStopDialogOpen(false)}
                PaperProps={{
                    sx: {
                        backgroundColor: retroColors.surface,
                        border: `1px solid ${vortxColors.error}66`,
                        boxShadow: `0 0 30px ${vortxColors.error}22`,
                    },
                }}
            >
                <DialogTitle sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    color: vortxColors.error,
                    textTransform: 'uppercase',
                    letterSpacing: '0.1em',
                }}>
                    Terminate Simulation?
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.75rem',
                        color: retroColors.muted,
                        lineHeight: 1.6,
                    }}>
                        This will stop the simulation server and discard the current run.
                        Any unsaved state will be lost.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={() => setStopDialogOpen(false)}
                        sx={{
                            color: retroColors.muted,
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleStop}
                        variant="contained"
                        sx={{
                            backgroundColor: vortxColors.error,
                            color: '#fff',
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            '&:hover': {
                                backgroundColor: '#c0392b',
                            },
                        }}
                    >
                        Stop
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
